/**
 * Phase 10d.3: "Share with Client" dialog.
 *
 * One-click sharing of a project's external portal view. Allows admins to:
 * - Select a panel template (simple / standard / enterprise) or custom panel selection
 * - Enter a client email (auto-suggests from scope contacts)
 * - Toggle can_share permission per invite
 * - Send an invitation (creates view + membership + email in one action)
 * - Copy the portal link
 * - View and manage existing members (revoke, resend)
 *
 * Feature-gated: only shown when the tenant's portal_management flag is on
 * (read via useFeature('portal_management') at the parent — see
 * docs/patterns/BACKEND_PATTERNS.md → "Feature Flag Gating").
 */
import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Users, Copy, Check, Send, X, RefreshCw, Link, ChevronDown, ChevronUp, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { api } from '@/lib/api';
import { isValidEmail } from '@/lib/email-validation';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';

// ── Types ─────────────────────────────────────────────────────────────────────

type Template = 'simple' | 'standard' | 'enterprise' | 'custom';

interface ScopeContact {
  contact_email?: string;
  contact_name?: string;
  organization_name?: string;
  is_client_type?: boolean;
}

interface Member {
  id: string;
  member_email: string;
  role: string;
  status: 'pending' | 'active' | 'revoked';
  invited_by: string;
  created_at: string;
}

interface ShareResult {
  view: { id: string; name: string; token: string };
  share_url: string;
  members: Member[];
  errors: Array<{ email: string; reason: string }>;
}

interface ShareWithClientDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
  projectName: string;
  /** Scope contacts to pre-suggest emails (optional) */
  scopeContacts?: ScopeContact[];
}

// ── Panel definitions ──────────────────────────────────────────────────────────

interface PanelDef {
  type: string;
  label: string;
}

const ALL_PANELS: PanelDef[] = [
  { type: 'project_summary',  label: 'Project Summary' },
  { type: 'deliverable_list', label: 'Deliverables' },
  { type: 'invoice_list',     label: 'Invoices' },
  { type: 'action_items',     label: 'Action Items' },
];

const TEMPLATE_PANELS: Record<Exclude<Template, 'custom'>, string[]> = {
  simple:     ['project_summary'],
  standard:   ['project_summary', 'deliverable_list', 'invoice_list'],
  enterprise: ['project_summary', 'deliverable_list', 'invoice_list', 'action_items'],
};

// ── Helpers ───────────────────────────────────────────────────────────────────

const TEMPLATE_LABELS: Record<Exclude<Template, 'custom'>, { label: string; description: string }> = {
  simple: {
    label: 'Simple',
    description: 'Project overview only',
  },
  standard: {
    label: 'Standard',
    description: 'Overview + Deliverables + Invoices',
  },
  enterprise: {
    label: 'Enterprise',
    description: 'All panels',
  },
};

function statusBadge(status: Member['status']) {
  if (status === 'active') return <Badge className="bg-green-100 text-green-700 border-green-200">Active</Badge>;
  if (status === 'pending') return <Badge className="bg-yellow-100 text-yellow-700 border-yellow-200">Pending</Badge>;
  return <Badge variant="outline" className="text-gray-500">Revoked</Badge>;
}

/** Derive which template preset matches a set of panel types, or 'custom'. */
function panelsToTemplate(panels: string[]): Template {
  const sorted = [...panels].sort().join(',');
  for (const [tmpl, types] of Object.entries(TEMPLATE_PANELS)) {
    if ([...types].sort().join(',') === sorted) return tmpl as Template;
  }
  return 'custom';
}

// ── Component ─────────────────────────────────────────────────────────────────

export function ShareWithClientDialog({
  open,
  onOpenChange,
  projectId,
  projectName,
  scopeContacts = [],
}: ShareWithClientDialogProps) {
  const queryClient = useQueryClient();

  // Form state
  const [email, setEmail] = useState('');
  const [template, setTemplate] = useState<Template>('standard');
  const [selectedPanels, setSelectedPanels] = useState<string[]>(TEMPLATE_PANELS['standard']);
  const [welcomeMessage, setWelcomeMessage] = useState('');
  const [showCustomPanels, setShowCustomPanels] = useState(false);
  const [sharing, setSharing] = useState(false);

  // Result state — persists after first share within a dialog session
  const [shareResult, setShareResult] = useState<ShareResult | null>(null);
  const [members, setMembers] = useState<Member[]>([]);
  const [copied, setCopied] = useState(false);

  // Per-member action state
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [resendingId, setResendingId] = useState<string | null>(null);

  // Suggested emails from scope contacts (client-type first)
  const suggestedEmails = scopeContacts
    .filter(s => s.contact_email)
    .sort((a, b) => (b.is_client_type ? 1 : 0) - (a.is_client_type ? 1 : 0))
    .map(s => ({ email: s.contact_email!, label: `${s.contact_name || ''} — ${s.organization_name || ''}`.trim() }));

  // Pre-load existing share state when dialog opens
  const { data: existingShare } = useQuery({
    queryKey: ['project-share', projectId],
    queryFn: () => api.projectShare.getForProject(projectId),
    enabled: open,
    staleTime: 0,
  });

  // Populate from existing share when data arrives
  useEffect(() => {
    if (!existingShare) return;
    if (existingShare.view) {
      setShareResult({
        view: existingShare.view,
        share_url: existingShare.share_url!,
        members: existingShare.members,
        errors: [],
      });
      setMembers(existingShare.members.filter((m: Member) => m.status !== 'revoked'));
      setWelcomeMessage(existingShare.view.welcome_message ?? '');
    }
  }, [existingShare]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setEmail('');
      setTemplate('standard');
      setSelectedPanels(TEMPLATE_PANELS['standard']);
      setWelcomeMessage('');
      setShowCustomPanels(false);
      setSharing(false);
      setShareResult(null);
      setMembers([]);
      setCopied(false);
    }
  }, [open]);

  // ── Panel / template sync ────────────────────────────────────────────────────

  const handleTemplateChange = (value: string) => {
    const t = value as Template;
    setTemplate(t);
    if (t !== 'custom') {
      setSelectedPanels(TEMPLATE_PANELS[t as Exclude<Template, 'custom'>]);
      setShowCustomPanels(false);
    } else {
      setShowCustomPanels(true);
    }
  };

  const handlePanelToggle = (panelType: string, checked: boolean) => {
    const next = checked
      ? [...selectedPanels, panelType]
      : selectedPanels.filter(p => p !== panelType);
    setSelectedPanels(next);
    setTemplate(panelsToTemplate(next));
  };

  // ── Actions ─────────────────────────────────────────────────────────────────

  const handleShare = async () => {
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      toast.error('Client email is required');
      return;
    }
    if (!isValidEmail(trimmedEmail)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setSharing(true);
    try {
      const payload: Parameters<typeof api.projectShare.share>[1] = {
        emails: [trimmedEmail],
        welcome_message: welcomeMessage.trim() || undefined,
      };
      if (template === 'custom') {
        payload.custom_panels = selectedPanels;
      } else {
        payload.template = template as Exclude<Template, 'custom'>;
      }

      const result = await api.projectShare.share(projectId, payload);

      setShareResult(result);
      setMembers(result.members.filter(m => m.status !== 'revoked'));
      setEmail('');

      const hasErrors = result.errors.length > 0;
      if (hasErrors) {
        const msg = result.errors[0]?.reason || 'Unknown error';
        toast.warning(`Share created but: ${msg}`);
      } else {
        toast.success(`Invite sent to ${trimmedEmail}`);
      }

      queryClient.invalidateQueries({ queryKey: ['external-views'] });
      queryClient.invalidateQueries({ queryKey: ['project-share', projectId] });
    } catch (error: any) {
      toast.error(error.message || 'Failed to share project');
    } finally {
      setSharing(false);
    }
  };

  const handleCopyLink = async () => {
    if (!shareResult?.share_url) return;
    try {
      await navigator.clipboard.writeText(shareResult.share_url);
      setCopied(true);
      toast.success('Link copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy link');
    }
  };

  const handleRevoke = async (member: Member) => {
    if (!shareResult?.view?.id) return;
    setRevokingId(member.id);
    try {
      await api.projectShare.revokeMember(shareResult.view.id, member.id);
      setMembers(prev => prev.filter(m => m.id !== member.id));
      toast.success(`Access revoked for ${member.member_email}`);
      queryClient.invalidateQueries({ queryKey: ['project-share', projectId] });
    } catch (error: any) {
      toast.error(error.message || 'Failed to revoke access');
    } finally {
      setRevokingId(null);
    }
  };

  const handleResend = async (member: Member) => {
    if (!shareResult?.view?.id) return;
    setResendingId(member.id);
    try {
      await api.projectShare.resendInvite(shareResult.view.id, { email: member.member_email });
      toast.success(`Invitation resent to ${member.member_email}`);
    } catch (error: any) {
      toast.error(error.message || 'Failed to resend invite');
    } finally {
      setResendingId(null);
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg overflow-y-auto max-h-[90vh]" data-testid="share-with-client-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Share with Client
          </DialogTitle>
          <DialogDescription>
            Give clients secure access to view project details, deliverables, and invoices.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">

          {/* Share link (shown after first successful share) */}
          {shareResult && (
            <div className="rounded-md border bg-muted/40 px-3 py-2">
              <p className="text-xs font-medium text-muted-foreground mb-1.5 flex items-center gap-1.5">
                <Link className="h-3 w-3" />
                Portal Link
              </p>
              <div className="flex items-center gap-2">
                <p className="text-sm text-foreground truncate flex-1" data-testid="portal-url">
                  {shareResult.share_url}
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 shrink-0"
                  onClick={handleCopyLink}
                  data-testid="copy-link-button"
                >
                  {copied ? <Check className="h-3.5 w-3.5 text-green-600" /> : <Copy className="h-3.5 w-3.5" />}
                </Button>
                <a
                  href={shareResult.share_url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center h-7 px-2 rounded-md border text-xs hover:bg-muted transition-colors shrink-0"
                  title="Preview portal in a new tab"
                  data-testid="preview-portal-button"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
              </div>
            </div>
          )}

          {/* Existing members (shown after first share, or if members list populated) */}
          {members.length > 0 && (
            <div>
              <p className="text-sm font-medium mb-2">People with access</p>
              <div className="space-y-1.5" data-testid="members-list">
                {members.map(member => (
                  <div
                    key={member.id}
                    className="flex items-center justify-between rounded-md border px-3 py-2 text-sm"
                    data-testid={`member-row-${member.member_email}`}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="truncate">{member.member_email}</span>
                      {statusBadge(member.status)}
                    </div>
                    <div className="flex items-center gap-1 ml-2 shrink-0">
                      {member.status === 'pending' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2 text-xs"
                          onClick={() => handleResend(member)}
                          disabled={resendingId === member.id}
                          title="Resend invite"
                          data-testid={`resend-${member.member_email}`}
                        >
                          <RefreshCw className="h-3 w-3" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2 text-xs text-destructive hover:text-destructive"
                        onClick={() => handleRevoke(member)}
                        disabled={revokingId === member.id}
                        title="Revoke access"
                        data-testid={`revoke-${member.member_email}`}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Invite section */}
          <div className="space-y-3">
            <p className="text-sm font-medium">
              {members.length > 0 ? 'Add another client' : 'Invite client'}
            </p>

            {/* Email input with suggestions */}
            <div className="space-y-1.5">
              <Label htmlFor="client-email">Client email *</Label>
              <Input
                id="client-email"
                type="email"
                placeholder="client@example.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleShare(); }}
                data-testid="client-email-input"
              />

              {/* Scope contact suggestions */}
              {suggestedEmails.length > 0 && !email && (
                <div className="flex flex-wrap gap-1.5 pt-0.5">
                  {suggestedEmails.slice(0, 4).map(({ email: e, label }) => (
                    <button
                      key={e}
                      type="button"
                      onClick={() => setEmail(e)}
                      className="inline-flex items-center rounded-full border px-2 py-0.5 text-xs text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                      title={label || e}
                      data-testid={`suggest-${e}`}
                    >
                      {e}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Template selector */}
            <div className="space-y-1.5">
              <Label htmlFor="template-select">Panel template</Label>
              <Select
                value={template}
                onValueChange={handleTemplateChange}
              >
                <SelectTrigger id="template-select" data-testid="template-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(Object.entries(TEMPLATE_LABELS) as [Exclude<Template, 'custom'>, typeof TEMPLATE_LABELS[Exclude<Template, 'custom'>]][]).map(
                    ([key, { label, description }]) => (
                      <SelectItem key={key} value={key}>
                        <span className="font-medium">{label}</span>
                        <span className="text-xs text-muted-foreground ml-1">— {description}</span>
                      </SelectItem>
                    )
                  )}
                  <SelectItem value="custom">
                    <span className="font-medium">Custom</span>
                    <span className="text-xs text-muted-foreground ml-1">— Choose panels</span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Panel checkboxes — shown when 'Custom' is selected OR collapsed toggle */}
            <div className="space-y-1.5">
              <button
                type="button"
                onClick={() => setShowCustomPanels(v => !v)}
                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
                data-testid="toggle-panel-checkboxes"
              >
                {showCustomPanels ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                {showCustomPanels ? 'Hide panels' : 'Customize panels'}
              </button>

              {showCustomPanels && (
                <div className="rounded-md border p-3 space-y-2" data-testid="panel-checkboxes">
                  {ALL_PANELS.map(panel => (
                    <div key={panel.type} className="flex items-center gap-2">
                      <Checkbox
                        id={`panel-${panel.type}`}
                        checked={selectedPanels.includes(panel.type)}
                        onCheckedChange={(checked) => handlePanelToggle(panel.type, Boolean(checked))}
                        data-testid={`panel-checkbox-${panel.type}`}
                      />
                      <label
                        htmlFor={`panel-${panel.type}`}
                        className="text-sm cursor-pointer"
                      >
                        {panel.label}
                      </label>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Welcome message */}
            <div className="space-y-1.5">
              <Label htmlFor="dialog-welcome-message">Welcome message (optional)</Label>
              <Textarea
                id="dialog-welcome-message"
                value={welcomeMessage}
                onChange={e => setWelcomeMessage(e.target.value)}
                placeholder="Welcome to your project portal! Feel free to review updates and reach out with any questions."
                className="resize-none text-sm"
                rows={2}
                maxLength={1000}
                data-testid="dialog-welcome-message"
              />
              <p className="text-xs text-muted-foreground">
                Shown at the top of the portal when clients log in.
              </p>
            </div>

            {/* Send button */}
            <Button
              className="w-full"
              onClick={handleShare}
              disabled={sharing || !email.trim()}
              data-testid="send-invite-button"
            >
              {sharing ? (
                'Sending...'
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Send Invite
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
